package dev.lingvo.shared.screenshots.text

import dev.lingvo.shared.screenshots.doc.FontNameRule
import dev.lingvo.shared.screenshots.doc.TypeRole

/** Pinned coverage and supported styles make font selection independent of host installations. */
data class FontFamily(val name: String, val roles: Set<TypeRole>, val scripts: Set<Script>, val weights: IntRange, val italic: Boolean)

/** The sole family allowlist for rendering also supplies validated Google Fonts requests. */
object FontCatalog {
    /** Every saved v1 family has metadata, including families offered only as alternatives. */
    val families: List<FontFamily> = fontMetadata.map { data ->
        val scripts = data.subsets.split(',').flatMap { subset -> when (subset) {
            "latin", "latin-ext" -> listOf(Script.LATIN)
            "vietnamese" -> listOf(Script.VIETNAMESE)
            "cyrillic", "cyrillic-ext" -> listOf(Script.CYRILLIC)
            "greek", "greek-ext" -> listOf(Script.GREEK)
            "arabic" -> listOf(Script.ARABIC, Script.URDU)
            "hebrew" -> listOf(Script.HEBREW)
            "devanagari" -> listOf(Script.DEVANAGARI)
            "bengali" -> listOf(Script.BENGALI)
            "gujarati" -> listOf(Script.GUJARATI)
            "gurmukhi" -> listOf(Script.GURMUKHI)
            "kannada" -> listOf(Script.KANNADA)
            "malayalam" -> listOf(Script.MALAYALAM)
            "tamil" -> listOf(Script.TAMIL)
            "telugu" -> listOf(Script.TELUGU)
            "thai" -> listOf(Script.THAI)
            "khmer" -> listOf(Script.KHMER)
            "myanmar" -> listOf(Script.MYANMAR)
            "chinese-simplified" -> listOf(Script.HANS)
            "chinese-traditional" -> listOf(Script.HANT)
            "chinese-hongkong" -> listOf(Script.HK)
            "japanese" -> listOf(Script.JAPANESE)
            "korean" -> listOf(Script.KOREAN)
            else -> emptyList()
        } }.toSet()
        val roles = when (data.category) {
            "Monospace" -> setOf(TypeRole.MONO)
            "Serif" -> setOf(TypeRole.SERIF, TypeRole.DISPLAY)
            else -> setOf(TypeRole.TEXT, TypeRole.DISPLAY)
        }
        FontFamily(data.name, roles, scripts, data.weights, data.italic)
    }
    private val index = families.associateBy { it.name }
    private val queries = fontMetadata.associate { it.name to it.query }

    /** Exact names prevent CSS syntax and arbitrary network requests from document content. */
    fun byName(name: String): FontFamily? = index[name]

    /** Validators can share the same catalog without introducing a dependency on the renderer. */
    val rule: FontNameRule = FontNameRule { it in index }

    /** Static faces use their actual weights; ranges are requested only for variable fonts. */
    fun googleFontsQuery(name: String): String? = queries[name]?.let { name.replace(" ", "+") + ":" + it }
}
